// Presence store (Layer B1): who in the org currently has Brizina open.
//
// Lives OUTSIDE the React tree (§9.3.1) — the presence socket writes here and
// components read snapshots. Updates arrive only on TRANSITIONS (a user comes
// online / goes offline / enters a call), never on a timer, so a directory of
// 200 people costs a handful of renders per hour.
//
// Metadata only: status, never content.

import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

import { DEFAULT_RINGTONE } from '../sounds';

export type PresenceStatus = 'online' | 'in_call' | 'dnd' | 'offline';

export interface PresenceView {
  /** Is our own presence socket up? (Dots are stale/unknown when false.) */
  connected: boolean;
  /** userId → status. Absent = offline. */
  statuses: Record<string, PresenceStatus>;
  /** Org incoming-call ringtone id, delivered with the socket's hello. */
  ringtone: string;
  /** OUR OWN Do Not Disturb. Other people's arrives as a status transition;
   * ours comes with hello so the toggle paints right on a fresh tab. */
  dnd: boolean;
}

const initial: PresenceView = { connected: false, statuses: {}, ringtone: DEFAULT_RINGTONE, dnd: false };

export const presenceStore = createStore<PresenceView>(() => ({ ...initial }));

export function usePresenceStore<T>(selector: (s: PresenceView) => T): T {
  return useStore(presenceStore, selector);
}

/** Status of one user (offline when unknown). */
export function statusOf(s: PresenceView, userId: string): PresenceStatus {
  return s.statuses[userId] ?? 'offline';
}

/** Replace the whole map (the socket's opening snapshot). */
export function setPresenceSnapshot(users: Record<string, PresenceStatus>): void {
  presenceStore.setState({ statuses: { ...users } });
}

/** Apply one transition. */
export function setPresence(userId: string, status: PresenceStatus): void {
  presenceStore.setState((s) => ({ statuses: { ...s.statuses, [userId]: status } }));
}

export function setSelfDnd(on: boolean): void {
  presenceStore.setState({ dnd: on });
}

export function setRingtone(id: string): void {
  presenceStore.setState({ ringtone: id || DEFAULT_RINGTONE });
}

export function setPresenceConnected(connected: boolean): void {
  presenceStore.setState({ connected });
}

/** Clear everything (sign-out / socket permanently down). */
export function resetPresence(): void {
  presenceStore.setState({ ...initial, statuses: {} });
}
